import "./StageStrip.css";
import { NO_TRACK, TRACKS } from "../chat/api";
import type { Session } from "../chat/api";
import { tx } from "../i18n";

/* ══ 단계 띠 ══
   채택 시안 안 B(Figma 38-2)의 축이다 — 띠가 장식이 아니라 **세션 목록으로 들어가는 길**이다.
   칸을 누르면 그 단계의 세션이 탭으로 선다.

   **화면이 판정하지 않는다.** 칸의 표식은 셋에서 온다:
   - 그 트랙에 **열린 세션**이 있다 → ● 도는 중
   - 큐(`GET /dispatch/queue`)가 이 이슈의 다음 트랙이라 한다 → ◔ 다음
   - 그 트랙의 **마지막 세션**이 어떻게 끝났나(`outcome`) → ✓ ok · ✕ 그 밖(이름 그대로)
   회수(N회)는 세션 기록의 수다. 색은 셋째 신호다 — 글리프와 글자가 늘 같이 간다. */

export type StageCell = {
  key: string;
  label: string;
  count: number;
  /** 열린 세션이 있다. */
  running: boolean;
  /** 큐가 「다음은 이 트랙」이라 한다. */
  next: boolean;
  /** 마지막 세션의 `outcome`. 세션이 없으면 null. */
  lastOutcome: string | null;
};

/** 세션 기록과 큐의 트랙으로 칸을 만든다. 모르는 트랙은 **버리지 않고** 이름 그대로 칸을 더한다. */
export function stageCells(sessions: Session[], nextTrack: string | null): StageCell[] {
  const keys: { key: string; label: string }[] = [...TRACKS];
  for (const s of sessions) {
    const k = s.track ?? NO_TRACK;
    if (!keys.some((x) => x.key === k)) keys.push({ key: k, label: k });
  }
  if (nextTrack && !keys.some((x) => x.key === nextTrack)) keys.push({ key: nextTrack, label: nextTrack });
  return keys.map(({ key, label }) => {
    const mine = sessions.filter((s) => (s.track ?? NO_TRACK) === key);
    const latest = [...mine].sort((a, b) => (b.startedAt ?? "").localeCompare(a.startedAt ?? ""))[0];
    return {
      key,
      label,
      count: mine.length,
      running: mine.some((s) => s.endedAt === null),
      next: nextTrack === key,
      lastOutcome: latest ? (latest.endedAt === null ? null : (latest.outcome ?? tx("chat.unknown"))) : null,
    };
  });
}

export function cellGlyph(c: StageCell): { glyph: string; word: string; tone: string } {
  if (c.running) return { glyph: "●", word: tx("issue.working"), tone: "running" };
  if (c.next) return { glyph: "◔", word: tx("common.action.next"), tone: "next" };
  if (c.count === 0) return { glyph: "○", word: tx("chat.stage.notYet"), tone: "idle" };
  if (c.lastOutcome === "ok") return { glyph: "✓", word: tx("chat.ended"), tone: "ok" };
  if (c.lastOutcome === "stopped") return { glyph: "■", word: tx("chat.stop"), tone: "bad" };
  return { glyph: "✕", word: c.lastOutcome ?? tx("chat.unknown"), tone: "bad" };
}

export function StageStrip({
  cells,
  selected,
  onSelect,
}: {
  cells: StageCell[];
  selected: string | null;
  onSelect: (key: string) => void;
}) {
  return (
    <ol className="stage" aria-label={tx("chat.stage.label")}>
      {cells.map((c) => {
        const g = cellGlyph(c);
        const on = selected === c.key;
        return (
          <li className="stage__item" key={c.key}>
            <button
              type="button"
              className={`stage__cell stage__cell--${g.tone}`}
              aria-pressed={on}
              aria-label={`${c.label} · ${g.word} · ${c.count ? tx("chat.stage.times", { n: c.count }) : tx("chat.stage.none")}`}
              title={`${c.label} — ${g.word}${c.count ? ` · ${tx("chat.stage.sessionTimes", { n: c.count })}` : ""}`}
              onClick={() => onSelect(c.key)}
            >
              <span className="stage__top">
                <span className="stage__glyph" aria-hidden="true">
                  {g.glyph}
                </span>
                <span className="stage__label">{c.label}</span>
              </span>
              <span className="stage__sub">{c.count ? tx("chat.stage.times", { n: c.count }) : "—"}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
